import React, { useEffect, useMemo, useState } from "react";
import { getDocumentConfig, DOCUMENT_TYPES } from "../config/DocumentConfig";
import { FileText, Printer, Search } from "lucide-react";

const BarcodePrintingModal = ({ config, invNo, onClose, onPrinted }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const docConfig = getDocumentConfig(DOCUMENT_TYPES.PURCHASE_INVOICE);

  useEffect(() => {
    if (!invNo) return;
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const detailPath = docConfig.apiEndpoints?.detail?.(
          config.COMPANY_CODE,
          invNo,
          config.DEPARTMENT_NO
        );
        const url = `${config.BASE_URL}${detailPath}`;
        const res = await fetch(url);
        if (!res.ok)
          throw new Error(`Failed to fetch document detail: ${res.status}`);
        const data = await res.json();

        // Map response rows to table rows:
        const mapped = (data || []).map((r, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: r.code || r.COD || r.Code || "",
          description: r.idesc || r.description || "",
          qty: Number(r.tr_qty2 ?? r.qty ?? r.QTY ?? 0),
          qty_print: Number(r.tr_qty2 ?? r.qty ?? r.QTY ?? 0), // initialize same as qty
          location: (r.locn || r.location || "").toString(),
          raw: r,
        }));

        setRows(mapped);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to fetch details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [
    invNo,
    config.BASE_URL,
    config.COMPANY_CODE,
    config.DEPARTMENT_NO,
    docConfig,
  ]);

  // derive location options from rows
  const locationOptions = useMemo(() => {
    const setLoc = new Set(
      rows.map((r) => (r.location || "UNASSIGNED").toString())
    );
    return ["ALL", ...Array.from(setLoc)];
  }, [rows]);

  // filtered rows by search + location
  const filteredRows = rows.filter((r) => {
    if (locationFilter !== "ALL" && r.location !== locationFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.code || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s) ||
      String(r.sino).includes(s)
    );
  });

  const updateQtyPrint = (id, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, qty_print: Math.max(0, Number(value) || 0) } : r
      )
    );
  };
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRows.map((r) => r.id)));
    }
  };
  const handlePrint = () => {
    // collect printable rows (qty_print > 0)
    const printable = rows.filter((r) => r.qty_print > 0);

    if (printable.length === 0) {
      alert("No items selected for printing (qty_print must be > 0).");
      return;
    }

    // Build printable HTML
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
      alert("Unable to open print window (popup blocked?).");
      return;
    }

    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 8px 6px; border: 1px solid #ddd; text-align: left; }
        th { background: #f3f4f6; }
        .header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .title { font-weight:700; font-size:1.1rem; }
      </style>
    `;

    const headerHtml = `
      <div class="header">
        <div class="title">Barcode Print - Invoice ${invNo}</div>
        <div>Printed: ${new Date().toLocaleString()}</div>
      </div>
    `;

    const tableRows = printable
      .map(
        (r) => `
      <tr>
        <td>${r.sino}</td>
        <td>${escapeHtml(r.code)}</td>
        <td style="text-align:right">${r.qty}</td>
        <td style="text-align:right">${r.qty_print}</td>
      </tr>`
      )
      .join("");

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          ${style}
        </head>
        <body>
          ${headerHtml}
          <table>
            <thead>
              <tr>
                <th>Si No</th>
                <th>Code</th>
                <th>Qty</th>
                <th>Qty Print</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    // wait a bit then call print
    setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
        // optionally close window after printing:
        // printWindow.close();
        if (onPrinted) onPrinted();
      } catch (err) {
        console.error("Print failed:", err);
      }
    }, 500);
  };

  // simple escape helper
  function escapeHtml(s) {
    if (!s) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
  const selectedCount = selectedIds.size;

  return (
    <div className=" p-3 space-y-3">
      <div className="pb-2 border-b border-gray-200 space-y-1">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
          >
            {locationOptions.map((loc) => (
              <option key={loc} value={loc}>
                {loc === "ALL" ? "All Locations" : loc}
              </option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            disabled={loading || selectedCount === 0}
            className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg flex items-center gap-2 hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm transition-all"
          >
            <Printer className="w-4 h-4" />
            Print Selected ({selectedCount})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-600">Loading invoice details...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="overflow-auto h-full rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        filteredRows.length > 0 &&
                        selectedIds.size === filteredRows.length
                      }
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Si No
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">
                    Qty
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">
                    Qty Print
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      No items found
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => (
                    <tr
                      key={r.id}
                      className={`hover:bg-blue-50 transition-colors ${
                        selectedIds.has(r.id) ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {r.sino}
                      </td>
                      <td className="px-4 py-3  text-sm font-semibold text-gray-900">
                        {r.code}
                      </td>
                      <td
                        className="px-4 py-3 text-gray-700 "
                        title={r.description}
                      >
                        {r.description}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {r.qty}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          value={r.qty_print}
                          min={0}
                          max={99999}
                          onChange={(e) => updateQtyPrint(r.id, e.target.value)}
                          className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {r.location || "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodePrintingModal;
